import { useState, useEffect } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { NativeSelect } from "@/components/ui/native-select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/errorMessage";
import { ListChecks, PackageOpen, MapPin, Save } from "lucide-react";

const fmtBRL = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

function ReclassificacaoTab() {
  const [itens, setItens] = useState([]);
  const [cats, setCats] = useState([]);
  const [salvando, setSalvando] = useState(false);
  useEffect(() => {
    axios.get(`${API}/patrimonio-f1/reclassificacao`).then(r => setItens(r.data)).catch(() => {});
    axios.get(`${API}/patrimonio-categorias`).then(r => setCats(r.data)).catch(() => {});
  }, []);
  const confirmar = async () => {
    setSalvando(true);
    try {
      const r = await axios.post(`${API}/patrimonio-f1/reclassificacao/confirmar`, {
        itens: itens.map(i => ({ id: i.id, categoria: i.categoria_sugerida })),
      });
      toast.success(r.data.message);
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao confirmar")); }
    finally { setSalvando(false); }
  };
  return (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm text-slate-600">{itens.length} itens — confira a categoria sugerida, ajuste e confirme em lote. <b>Nada é gravado sem confirmação</b> (backup automático antes).</p>
          <Button size="sm" onClick={confirmar} disabled={salvando || !itens.length} className="bg-luvcorp-navy text-white" data-testid="btn-confirmar-reclassificacao">
            <Save className="h-4 w-4 mr-1" />{salvando ? "Gravando…" : `Confirmar ${itens.length} itens`}
          </Button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto divide-y">
          {itens.map((i, idx) => (
            <div key={i.id} className="flex items-center gap-3 py-1.5 text-sm">
              <span className="font-mono text-xs text-slate-400 w-20 shrink-0">{i.codigo_patrimonio}</span>
              <span className="flex-1 truncate">{i.nome}</span>
              <div className="w-56 shrink-0">
                <NativeSelect value={i.categoria_sugerida}
                  onValueChange={(v) => setItens(arr => arr.map((x, j) => j === idx ? { ...x, categoria_sugerida: v } : x))}
                  options={cats.map(c => ({ value: c.nome, label: c.nome }))}
                  data-testid={`select-reclass-${i.codigo_patrimonio}`} />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function EstoqueTab() {
  const [dados, setDados] = useState({ sugeridos: [], sugeridos_retornaveis: [], duplicados: [] });
  const [sel, setSel] = useState(new Set());
  const [selRet, setSelRet] = useState(new Set());
  const [salvando, setSalvando] = useState(false);
  const [salvandoRet, setSalvandoRet] = useState(false);
  const load = () => axios.get(`${API}/patrimonio-f1/estoque-duraveis`).then(r => setDados(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const transferir = async () => {
    if (!window.confirm(`Transferir ${sel.size} material(is) para o patrimônio? O saldo será zerado com movimentação "Transferido para patrimônio" (backup automático antes).`)) return;
    setSalvando(true);
    try {
      const r = await axios.post(`${API}/patrimonio-f1/transferir-para-patrimonio`, { material_ids: [...sel] });
      toast.success(r.data.message);
      setSel(new Set()); load();
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao transferir")); }
    finally { setSalvando(false); }
  };
  return (
    <div className="space-y-4">
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm text-slate-600">Itens <b>duráveis</b> sugeridos para virar patrimônio (1 item por unidade em estoque). Desgaste rápido e EPIs descartáveis continuam como material.</p>
            <Button size="sm" onClick={transferir} disabled={salvando || sel.size === 0} className="bg-luvcorp-navy text-white" data-testid="btn-transferir-patrimonio">
              <PackageOpen className="h-4 w-4 mr-1" />{salvando ? "Transferindo…" : `Transferir ${sel.size} selecionado(s)`}
            </Button>
          </div>
          <div className="max-h-[45vh] overflow-y-auto divide-y">
            {dados.sugeridos.map((m) => (
              <label key={m.id} className="flex items-center gap-3 py-1.5 text-sm cursor-pointer">
                <Checkbox checked={sel.has(m.id)} onCheckedChange={(v) => {
                  const n = new Set(sel); v ? n.add(m.id) : n.delete(m.id); setSel(n);
                }} data-testid={`check-transferir-${m.codigo}`} />
                <span className="font-mono text-xs text-slate-400 w-20 shrink-0">{m.codigo}</span>
                <span className="flex-1 truncate">{m.nome}</span>
                <Badge variant="outline" className="text-[10px]">{m.quantidade_atual} em estoque</Badge>
                <span className="text-xs text-slate-500 w-24 text-right">{fmtBRL(m.valor_unitario)}/un</span>
              </label>
            ))}
            {dados.sugeridos.length === 0 && <p className="text-sm text-slate-400 py-4">Nenhum item durável pendente.</p>}
          </div>
        </CardContent>
      </Card>
      {(dados.sugeridos_retornaveis || []).length > 0 && (
        <Card className="border-0 shadow-sm border-l-4 border-l-teal-500">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-sm text-slate-600"><b>Sugerido: marcar como Retornável</b> — ferramenta miúda abaixo de {fmtBRL(dados.valor_minimo || 150)}/un (volta da obra, mas não vira patrimônio unitário).</p>
              <Button size="sm" variant="outline" className="border-teal-500 text-teal-700" disabled={salvandoRet || selRet.size === 0}
                data-testid="btn-marcar-retornavel"
                onClick={async () => {
                  setSalvandoRet(true);
                  try {
                    const r = await axios.post(`${API}/estoque/controle-lote`, { ids: [...selRet], controle: "retornavel" });
                    toast.success(r.data.message); setSelRet(new Set()); load();
                  } catch (err) { toast.error(extractErrorMessage(err, "Erro ao marcar")); }
                  finally { setSalvandoRet(false); }
                }}>
                {salvandoRet ? "Marcando…" : `Marcar ${selRet.size} como Retornável`}
              </Button>
            </div>
            <div className="max-h-[35vh] overflow-y-auto divide-y">
              {dados.sugeridos_retornaveis.map((m) => (
                <label key={m.id} className="flex items-center gap-3 py-1.5 text-sm cursor-pointer">
                  <Checkbox checked={selRet.has(m.id)} onCheckedChange={(v) => {
                    const n = new Set(selRet); v ? n.add(m.id) : n.delete(m.id); setSelRet(n);
                  }} data-testid={`check-retornavel-${m.codigo}`} />
                  <span className="font-mono text-xs text-slate-400 w-20 shrink-0">{m.codigo}</span>
                  <span className="flex-1 truncate">{m.nome}</span>
                  <Badge variant="outline" className="text-[10px]">{m.quantidade_atual} em estoque</Badge>
                  <span className="text-xs text-slate-500 w-24 text-right">{fmtBRL(m.valor_unitario)}/un</span>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      {dados.duplicados.length > 0 && (
        <Card className="border-0 shadow-sm border-l-4 border-l-amber-400">
          <CardContent className="p-4 space-y-2">
            <p className="text-sm font-semibold text-amber-800">{dados.duplicados.length} possível(is) duplicado(s) no estoque (mesmo nome normalizado) — unificação assistida na Fase 2 do cadastro:</p>
            {dados.duplicados.map((d, i) => (
              <p key={i} className="text-xs text-slate-600">• {d.itens.map(x => `${x.codigo} (${x.quantidade_atual})`).join("  +  ")} — <b>{d.itens[0].nome}</b></p>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function RevisarTab() {
  const [dados, setDados] = useState({ a_revisar: [], inconsistentes: [] });
  useEffect(() => { axios.get(`${API}/patrimonio-f1/localizacao-a-revisar`).then(r => setDados(r.data)).catch(() => {}); }, []);
  const Lista = ({ titulo, itens, cor }) => (
    <Card className={`border-0 shadow-sm border-l-4 ${cor}`}>
      <CardContent className="p-4 space-y-1">
        <p className="text-sm font-semibold text-luvcorp-navy">{titulo} ({itens.length})</p>
        {itens.map(i => (
          <p key={i.id} className="text-xs text-slate-600">
            <span className="font-mono text-slate-400">{i.codigo_patrimonio}</span> {i.nome} — <i>{i.localizacao_descricao || "sem localização"}</i> ({i.situacao})
          </p>
        ))}
        {itens.length === 0 && <p className="text-xs text-slate-400">Nenhum item.</p>}
      </CardContent>
    </Card>
  );
  return (
    <div className="space-y-4">
      <Lista titulo="Localização a revisar (texto livre, sem local estruturado)" itens={dados.a_revisar} cor="border-l-amber-400" />
      <Lista titulo='Inconsistências: "Disponível" com responsável/colaborador' itens={dados.inconsistentes} cor="border-l-red-400" />
      <p className="text-xs text-slate-500">Para corrigir: abra o item no Patrimônio e faça uma movimentação para o local correto.</p>
    </div>
  );
}

export default function PatrimonioOrganizarPage() {
  return (
    <MainLayout>
      <div className="space-y-5" data-testid="pagina-organizar-patrimonio">
        <div>
          <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">Organizar Patrimônio — Fase 1</h1>
          <p className="text-slate-500 text-sm">Reclassificação assistida de categorias, transferência de duráveis do estoque e revisão de localizações. Toda migração gera backup e fica no histórico.</p>
        </div>
        <Tabs defaultValue="reclass">
          <TabsList>
            <TabsTrigger value="reclass" data-testid="tab-reclassificacao"><ListChecks className="h-4 w-4 mr-1" />Reclassificação</TabsTrigger>
            <TabsTrigger value="estoque" data-testid="tab-estoque-duraveis"><PackageOpen className="h-4 w-4 mr-1" />Estoque → Patrimônio</TabsTrigger>
            <TabsTrigger value="revisar" data-testid="tab-localizacao-revisar"><MapPin className="h-4 w-4 mr-1" />Localização a revisar</TabsTrigger>
          </TabsList>
          <TabsContent value="reclass"><ReclassificacaoTab /></TabsContent>
          <TabsContent value="estoque"><EstoqueTab /></TabsContent>
          <TabsContent value="revisar"><RevisarTab /></TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
}
